import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatPrice, orderStatusNames, paymentMethodNames } from '../utils/helpers';
import { User, Package, LogOut } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/bejelentkezes');
      return;
    }
    fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="relative min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="section-title text-4xl md:text-5xl mb-8 text-center">
          <span>Profil</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* User Info */}
          <div className="lg:col-span-1">
            <div className="glass rounded-xl p-6 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-yellow-200/20 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-yellow-200" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">{user.name}</h2>
                  <p className="text-gray-400 text-sm">{user.email}</p>
                </div>
              </div>

              {user.phone && (
                <div>
                  <p className="text-sm text-gray-400">Telefon</p>
                  <p className="text-white">{user.phone}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-400">Szerepkör</p>
                <p className="text-white">{user.role === 'admin' ? 'Adminisztrátor' : 'Felhasználó'}</p>
              </div>

              {user.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  className="w-full btn-magic rounded-lg py-3 font-semibold"
                >
                  Admin Dashboard
                </button>
              )}

              <button
                onClick={handleLogout}
                className="w-full glass rounded-lg py-3 hover:bg-white/5 transition flex items-center justify-center gap-2"
              >
                <LogOut className="w-5 h-5" />
                Kijelentkezés
              </button>
            </div>
          </div>

          {/* Orders */}
          <div className="lg:col-span-2">
            <div className="glass rounded-xl p-6">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Package className="w-6 h-6 text-yellow-200" />
                Rendeléseim
              </h2>

              {loading ? (
                <p className="text-gray-400 text-center py-8">Betöltés...</p>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 mx-auto mb-4 text-gray-500" />
                  <p className="text-gray-400 mb-4">Még nincs rendelésed</p>
                  <button
                    onClick={() => navigate('/termekek')}
                    className="btn-magic rounded-lg px-6 py-2"
                  >
                    Vásárlás kezdése
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.id} className="bg-black/20 rounded-lg p-4 border border-white/5">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                        <div>
                          <p className="font-semibold">Rendelés #{order.order_number}</p>
                          <p className="text-sm text-gray-400">
                            {new Date(order.created_at).toLocaleDateString('hu-HU')}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 mt-2 md:mt-0">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            order.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                            order.status === 'shipped' ? 'bg-blue-500/20 text-blue-400' :
                            order.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {orderStatusNames[order.status]}
                          </span>
                          <span className="text-sm text-gray-400">
                            {paymentMethodNames[order.payment_method]}
                          </span>
                        </div>
                      </div>

                      <div className="border-t border-white/5 pt-3 mt-3">
                        <div className="flex flex-wrap gap-2 mb-2">
                          {order.items.slice(0, 3).map((item, idx) => (
                            <span key={idx} className="text-sm text-gray-400">
                              {item.name} × {item.quantity}
                            </span>
                          ))}
                          {order.items.length > 3 && (
                            <span className="text-sm text-gray-400">
                              és még {order.items.length - 3} termék...
                            </span>
                          )}
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xl font-bold text-yellow-200">
                            {formatPrice(order.total)}
                          </span>
                          <button
                            onClick={() => navigate(`/rendeles/${order.id}`)}
                            className="text-sm text-yellow-200 hover:text-yellow-300 transition"
                          >
                            Részletek →
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .section-title {
          font-family: "Great Vibes", cursive;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
        .section-title span {
          color: #ffffff;
        }
        .btn-magic {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          border: 1px solid hsl(0 0% 100% / 0.14);
        }
        .btn-magic:before {
          content: '';
          position: absolute;
          inset: -200%;
          background: conic-gradient(from 0deg, transparent, hsl(45 86% 62% / 0.25), transparent 30%);
          animation: spin 6s linear infinite;
          z-index: -1;
          filter: blur(12px);
        }
        @keyframes spin {
          to { transform: rotate(1turn); }
        }
      `}</style>
    </div>
  );
};

export default Profile;